import type { DateTime } from "luxon"

import type { AppLocalization } from "../../../../../localization/appLocalization"
import type { DateTimeFactory } from "../../../../../localization/useDateTime"
import { ResourceType } from "../../../../admin/organization/resources/resourcesTypes"
import { GuestInfoState, GuestInfoType } from "../../../guestList/guestListTypes"
import type { GuestInfo } from "../../../guestList/guestListTypes"
import type { ServiceCenterBooking } from "../../serviceCenterHomeTypes"
import { formatMeetingRowTimes, toResourceTypes } from "../meetigs/helpers"

const DAY_START_HOUR = 8
const DAY_LAST_HOUR = 15

export type HourSlot = Readonly<{
    hour: number
    meetings: number
    guests: number
}>

export function countGuestPeople(guests: ReadonlyArray<GuestInfo>): number {
    return guests.reduce((total, guest) => total + (guest.type === GuestInfoType.GROUP ? guest.attendee.count : 1), 0)
}

export function toGuestsInHouse(guests: ReadonlyArray<GuestInfo>): GuestInfo[] {
    return guests.filter((guest) => guest.state === GuestInfoState.CHECKED_IN)
}

function toValidHour(dateTime: DateTime | undefined): number | undefined {
    return dateTime?.isValid ? dateTime.hour : undefined
}

export function toHourSlots(
    bookings: ReadonlyArray<ServiceCenterBooking>,
    guests: ReadonlyArray<GuestInfo>,
    dateTimeFactory: DateTimeFactory,
): HourSlot[] {
    const meetingHours = bookings
        .filter((booking) => toResourceTypes(booking.bookingDetails?.resources ?? []).includes(ResourceType.ROOM))
        .map((booking) => toValidHour(dateTimeFactory.fromApiOrUndefined(booking.meetingDetails.startDateTime)))
        .filter((hour): hour is number => hour !== undefined)

    const guestHours = guests.flatMap((guest) => {
        const hour = toValidHour(
            dateTimeFactory.fromApiOrUndefined(guest.timeline.meetingTime ?? guest.timeline.arrivalTime),
        )
        return hour === undefined ? [] : [{ hour, people: countGuestPeople([guest]) }]
    })

    const allHours = [...meetingHours, ...guestHours.map(({ hour }) => hour)]
    const firstHour = Math.min(DAY_START_HOUR, ...allHours)
    const lastHour = Math.max(DAY_LAST_HOUR, ...allHours)

    return Array.from({ length: lastHour - firstHour + 1 }, (_, index) => {
        const hour = firstHour + index
        return {
            hour,
            meetings: meetingHours.filter((meetingHour) => meetingHour === hour).length,
            guests: guestHours
                .filter((guestHour) => guestHour.hour === hour)
                .reduce((total, guestHour) => total + guestHour.people, 0),
        }
    })
}

export type GuestListRow = Readonly<{
    key: string
    name: string
    groupSize?: number
    company: string
    meeting: string
    host: string
    room: string
    start?: DateTime
    end?: DateTime
}>

export function toGuestListRows(
    guests: ReadonlyArray<GuestInfo>,
    bookings: ReadonlyArray<ServiceCenterBooking>,
    dateTimeFactory: DateTimeFactory,
): GuestListRow[] {
    return guests.map((guest, index) => {
        const booking = bookings.find((candidate) => candidate.id === guest.order?.id)
        const start = dateTimeFactory.fromApiOrUndefined(
            booking?.meetingDetails.startDateTime ?? guest.timeline.meetingTime,
        )
        const end = dateTimeFactory.fromApiOrUndefined(booking?.meetingDetails.endDateTime)

        return {
            key: `${guest.attendee.id}-${index}`,
            name: guest.attendee.name ?? "",
            groupSize: guest.type === GuestInfoType.GROUP ? guest.attendee.count : undefined,
            company: guest.attendee.company,
            meeting: guest.order?.meetingDetails.meetingName ?? booking?.meetingDetails.meetingName ?? "",
            host: guest.host.displayName,
            room: booking?.meetingDetails.location ?? "",
            start: start?.isValid ? start : undefined,
            end: end?.isValid ? end : undefined,
        }
    })
}

export type GuestListDisplayRow = Readonly<{
    key: string
    name: string
    company: string
    meeting: string
    host: string
    room: string
    time: string
}>

export function toGuestListDisplayRow(
    row: GuestListRow,
    l10n: AppLocalization,
    dateTimeFactory: DateTimeFactory,
): GuestListDisplayRow {
    const groupLabel =
        row.groupSize === undefined
            ? undefined
            : l10n.getString("service-center-reports-dashboard-guests-overview-group-label", { count: row.groupSize })
    const { start, end } = formatMeetingRowTimes(row.start, row.end, dateTimeFactory)
    const time = [start, end].filter(Boolean).join(" - ")

    return {
        key: row.key,
        name: [row.name, groupLabel].filter(Boolean).join(" · "),
        company: row.company,
        meeting: row.meeting,
        host: row.host,
        room: row.room,
        time,
    }
}
